import { PermissionEnum } from '@Application/Security/Roles';
import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import ADMINISTRATION_SECTIONS from '@UI/Routing/administrationSections';
import FACILITY_SECTIONS from '@UI/Routing/facilitySections';
import type { NavigationSection } from '@UI/Routing/navigationSection';
import {
	Activity,
	Archive,
	Briefcase,
	Building2,
	Cpu,
	Euro,
	FileText,
	FolderCog,
	Gauge,
	House,
	IdCard,
	KeyRound,
	Landmark,
	Layers,
	type LucideIcon,
	ScrollText,
	ShieldCheck,
	Sparkles,
	UserCog,
	Users,
} from 'lucide-react';

export type NavigationMenuEntryId =
	| 'home'
	| 'beneficiaries'
	| 'workflow-runs'
	| 'organizations'
	| 'document-groups'
	| 'facilities'
	| 'staff-members'
	| 'users'
	| 'ai-models'
	| 'ai-engines'
	| 'ai-costs'
	| 'ai-cost-limits'
	| 'document-templates'
	| 'subject-types'
	| 'roles'
	| 'sso'
	| 'retention'
	| 'audit-trail'
	| `subject:${string}`;

export type NavigationMenuDestination = {
	readonly path: string;
	/** Level label shown by `LevelSwitch`; the entry label for a single-destination entry. */
	readonly label: string;
	readonly icon: LucideIcon;
	/** Any one of them grants access; empty means always accessible. */
	readonly permissions: readonly PermissionEnum[];
	/** Reachable only when the caller's level on the beneficiary type allows reading its dossiers. */
	readonly requiresBeneficiaryRead?: true;
};

export type NavigationMenuEntry = {
	readonly id: NavigationMenuEntryId;
	readonly label: string;
	readonly icon: LucideIcon;
	readonly destinations: readonly NavigationMenuDestination[];
};

export type NavigationMenuSectionId = 'accompaniment' | 'organization' | 'configuration';

export type NavigationMenuSection = {
	readonly id: NavigationMenuSectionId;
	readonly label: string;
	readonly entries: readonly NavigationMenuEntry[];
};

const SECTIONS_BY_PATH: ReadonlyMap<string, NavigationSection<string>> = new Map<
	string,
	NavigationSection<string>
>([...ADMINISTRATION_SECTIONS, ...FACILITY_SECTIONS].map((section) => [section.path, section]));

/** Fails at module load when a menu path is missing from the route catalogues. */
const resolveSection = (path: string): NavigationSection<string> => {
	const section = SECTIONS_BY_PATH.get(path);

	if (section === undefined) {
		throw new Error(`Navigation menu references an unknown section: ${path}`);
	}

	return section;
};

/** Destination whose path and permission come from the route catalogues, never restated here. */
const buildDestination = (path: string, label: string, icon: LucideIcon): NavigationMenuDestination => {
	return { path, label, icon, permissions: [resolveSection(path).permission] };
};

/**
 * Menu in display order: sections, then entries, then destinations. An entry with several
 * destinations is one object seen at account / organization / agency level.
 */
const NAVIGATION_MENU: readonly NavigationMenuSection[] = [
	{
		id: 'accompaniment',
		label: 'Accompagnement',
		entries: [
			{
				id: 'home',
				label: 'Accueil',
				icon: House,
				destinations: [{ path: '/', label: 'Accueil', icon: House, permissions: [] }],
			},
			{
				id: 'beneficiaries',
				label: 'Bénéficiaires',
				icon: Users,
				destinations: [
					{
						path: buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY),
						label: 'Bénéficiaires',
						icon: Users,
						permissions: [],
						requiresBeneficiaryRead: true,
					},
				],
			},
			{
				id: 'workflow-runs',
				label: 'Traitements',
				icon: Activity,
				destinations: [
					{
						path: '/workflow-runs',
						label: 'Traitements',
						icon: Activity,
						permissions: [PermissionEnum.WORKFLOW_RUN_FACILITY_READ],
					},
				],
			},
		],
	},
	{
		id: 'organization',
		label: 'Structure',
		entries: [
			{
				id: 'organizations',
				label: 'Structures',
				icon: Landmark,
				destinations: [buildDestination('/administration/organizations', 'Structures', Landmark)],
			},
			{
				id: 'document-groups',
				label: 'Groupes de documents',
				icon: Layers,
				destinations: [
					buildDestination('/administration/document-groups', 'Groupes de documents', Layers),
				],
			},
			{
				id: 'facilities',
				label: 'Agences',
				icon: Building2,
				destinations: [buildDestination('/facilities', 'Agences', Building2)],
			},
			{
				id: 'staff-members',
				label: 'Personnel',
				icon: IdCard,
				destinations: [buildDestination('/facilities/staff-members', 'Personnel', IdCard)],
			},
			{
				id: 'users',
				label: 'Utilisateurs',
				icon: UserCog,
				destinations: [buildDestination('/administration/users', 'Utilisateurs', UserCog)],
			},
		],
	},
	{
		id: 'configuration',
		label: 'Configuration',
		entries: [
			{
				id: 'ai-models',
				label: "Modèles d'IA",
				icon: Sparkles,
				destinations: [
					buildDestination('/administration/account-ai-assignments', 'Compte', Briefcase),
					buildDestination('/administration/organization-ai-assignments', 'Structure', Landmark),
					buildDestination('/facilities/ai-assignments', 'Agence', Building2),
				],
			},
			{
				id: 'ai-engines',
				label: "Moteurs d'IA",
				icon: Cpu,
				destinations: [buildDestination('/administration/ai-engines', "Moteurs d'IA", Cpu)],
			},
			{
				id: 'ai-costs',
				label: "Coûts d'IA",
				icon: Euro,
				destinations: [buildDestination('/administration/ai-costs', "Coûts d'IA", Euro)],
			},
			{
				id: 'ai-cost-limits',
				label: "Plafonds de coûts d'IA",
				icon: Gauge,
				destinations: [
					buildDestination('/administration/ai-cost-limits', "Plafonds de coûts d'IA", Gauge),
				],
			},
			{
				id: 'document-templates',
				label: 'Gabarits',
				icon: FileText,
				destinations: [
					buildDestination('/administration/document-templates', 'Structure', Landmark),
					buildDestination('/facilities/document-templates', 'Agence', Building2),
				],
			},
			{
				id: 'subject-types',
				label: 'Types de dossier',
				icon: FolderCog,
				destinations: [
					buildDestination('/administration/subject-types', 'Types de dossier', FolderCog),
				],
			},
			{
				id: 'roles',
				label: 'Rôles',
				icon: ShieldCheck,
				destinations: [buildDestination('/administration/roles', 'Rôles', ShieldCheck)],
			},
			{
				id: 'sso',
				label: "Fournisseur d'identité",
				icon: KeyRound,
				destinations: [buildDestination('/administration/sso', "Fournisseur d'identité", KeyRound)],
			},
			{
				id: 'retention',
				label: 'Rétention & purge',
				icon: Archive,
				destinations: [buildDestination('/administration/retention', 'Rétention & purge', Archive)],
			},
			{
				id: 'audit-trail',
				label: "Journal d'audit",
				icon: ScrollText,
				destinations: [
					buildDestination('/administration/audit-trail', "Journal d'audit", ScrollText),
				],
			},
		],
	},
];

/** Sections gathered in the desktop settings panel, opened from the top bar's gear. */
export const SETTINGS_SECTION_IDS: readonly NavigationMenuSectionId[] = ['organization', 'configuration'];

export default NAVIGATION_MENU;
